import {gql} from "@apollo/client";
import {OrderDetailType} from "../../../../../graphql/types/OrderDetailType";

/** 客户修改包裹信息快照的字段选择：展示只用公制字段与单位制（plan 4.7 决策 1A） */
const PACKAGE_INFO_SNAPSHOT_FIELDS = `
                        useImperialUnit
                        weightKg
                        lengthCm
                        widthCm
                        heightCm
                        volumetricWeightKg`;

/**
 * 内部轨迹中客户修改包裹信息的修改前后对比字段选择。
 * 须声明在 ORDER_DETAIL_FIELDS 之前（const 不能在声明前引用）。
 */
const PACKAGE_INFO_CHANGE_FIELDS = `
                packageInfoChange{
                    weightChanged
                    dimensionsChanged
                    before{${PACKAGE_INFO_SNAPSHOT_FIELDS}
                    }
                    after{${PACKAGE_INFO_SNAPSHOT_FIELDS}
                    }
                }`;

/**
 * 订单详情的字段选择集。
 *
 * 抽成常量供「包裹详情」与「历史包裹详情」两个 query 共用——两者返回同一个 OrderDetailType，
 * 字段清单维护两份必然漂移。
 */
export const ORDER_DETAIL_FIELDS = `
            orderId
            settlementMode
            orderWithAddresses{
                order{
                    id
                    securityRnd
                    trackingNumber
                    channelId
                    channelOrderNumber
                    orderTime
                    overallStatus
                    isDelivered
                    isIntercepted
                    isAbnormal
                    exceptionStatus
                    warehouseLocationCode
                    storageNumber
                    expectedDeliveryTime
                    deliveredAt
                    deliveryAttempts
                    exceptionReasonCode
                    exceptionSubReasons
                    senderAddressId
                    recipientAddressId
                    receiverZipcode
                    sortingCode
                    temuDangerousGoodsSegment
                    temuBusinessTypeSegment
                    zone
                    newZone
                    weightKg
                    volumetricWeightKg
                    lengthCm
                    widthCm
                    heightCm
                    packageValueUsd
                    originalWeightKg
                    originalLengthCm
                    originalWidthCm
                    originalHeightCm
                    podReviewStatus
                    podReviewMessage {
                        id
                        failureOtherReason
                        lang
                        translateFailureOtherReason {
                            lang
                            translateFailureOtherReason
                        }
                    }
                    podImageS3KeysJson
                    exceptionImageS3KeysJson
                    isReceiverAddressModified
                    isDestHubArrived
                    isDestWarehouseArrived
                    destHubArrivedAt
                    destWarehouseArrivedAt
                    receivedAtHub
                    deliverAt
                    isComplaint
                    hasManded
                    isFinished
                    podImageS3UrlsJson
                    exceptionImageS3UrlsJson
                    nextAction
                    isHasBeenSetException
                    lastBagTaskId
                    bagTaskNumber
                }
                pickupAddress{
                    id
                    addressLine1
                    addressLine2
                    city
                    state
                    zipcode
                    fullName
                    phoneNumber
                    countryCode
                    extension
                    email
                    company
                    latitude
                    longitude
                    instructions
                }
                deliveryAddress{
                    id
                    addressLine1
                    addressLine2
                    city
                    state
                    zipcode
                    fullName
                    phoneNumber
                    countryCode
                    extension
                    email
                    company
                    latitude
                    longitude
                    instructions
                }
                returnAddress {
                    id
                    channelId
                    channelOrderNumber
                    externalTrackingNumber
                    trackingNumber
                    addressType
                    addressLine1
                    addressLine2
                    district
                    city
                    stateProvince
                    postalCode
                    countryCode
                    fullName
                    phoneNumber
                    email
                }
                originalRecipientAddress {
                    id
                    channelId
                    channelOrderNumber
                    externalTrackingNumber
                    trackingNumber
                    addressType
                    addressLine1
                    addressLine2
                    district
                    city
                    stateProvince
                    postalCode
                    countryCode
                    fullName
                    phoneNumber
                    email
                }
            }
            orderNote{
                note
                lang
                orderNoteTranslations {
                    translateNote
                    lang
                }
            }
            driverDeliveryNote{
                note
                lang
                orderNoteTranslations {
                    translateNote
                    lang
                }
            }
            clientCartonNo
            sortingChannelOrderNumber
            interceptionExemptionAlert
            externalOrderEvents {
                eventCode
                eventTime
                localGmtOffset
                description
                location
                reasonCode
                operatorAccountName
                sourceOrderEventId
            }
            orderEvents{
                eventId
                time
                type
                title
                details
                imageS3UrlsJson
                cameraPhotos {
                    s3Key
                    cameraType
                    reviewResults
                }
                nextAction
                exceptionReasonCode
                exceptionSubReasons
                channelId
                channelApiVersion
                phoneNumber
                failReason
                podComment
                previousStatus
                previousReviewNotes
                newStatus
                operationSource
                appointmentNumber
                driverAccountId
                driverName
                driverPhone
                dspId
                dspName
                operatorName
                operatorPhone
                operatorRole
                operatorWarehouseName
                operatorDspName
                pickupHubName
                sortingMachineCode
                sortingMachineType
                currentWarehouseName
                bagNumber
                clientCartonNo
                presortCode
                originalScanAction
                bagSortingCode
                scanSource
                loadRemoveType
                bagTaskName
                fromBagNumber
                toBagNumber
                batchName
                fromBatchName
                toBatchName
                reason
                oldSortingRule
                newSortingRule
                licensePlate
                sealNo
                deliveryAttempts
                deliveryDistance
                deliveryMethod
                revokeReason
                latitude
                longitude
                weightKg
                originalEventType
                isDestHubArrived
                scStages
                externalBusinessType
                bagContainerId
                bagContainerName
                fromBagChange
                isFirstLmArrival
                operatePlatformType
                messageKey
                notes
                departureEventType
                carrierName
                oldRecipientAddress {
                   id
                   fullName
                   addressLine1
                   addressLine2
                   city
                   state
                   zipcode
                   phoneNumber
                   email
                   countryCode
                   extension
                   company
                   instructions
                }
                newRecipientAddress {
                   id
                   fullName
                   addressLine1
                   addressLine2
                   city
                   state
                   zipcode
                   phoneNumber
                   email
                   countryCode
                   extension
                   company
                   instructions
                }
                oldFullName
                oldPhoneCountryCode
                oldPhoneNumber
                oldPhoneExtension
                newFullName
                newPhoneCountryCode
                newPhoneNumber
                newPhoneExtension
                oldOrderNote{
                    note
                    lang
                    orderNoteTranslations {
                        translateNote
                        lang
                    }
                }
                newOrderNote{
                    note
                    lang
                    orderNoteTranslations {
                        translateNote
                        lang
                    }
                }
                driverDeliveryNote{
                    note
                    lang
                    orderNoteTranslations {
                        translateNote
                        lang
                    }
                }
                oldLengthCm
                oldWidthCm
                oldHeightCm
                newLengthCm
                newWidthCm
                newHeightCm
                oldLatitude
                oldLongitude
                newLatitude
                newLongitude
                addressLocationReviewStatus${PACKAGE_INFO_CHANGE_FIELDS}
            }
            deliveryInfo {
                stopNumber
                subStopNumber
                driverAccountId
                driverName
                driverPhone
                taskDate
                routeName
                deliveryLatitude
                deliveryLongitude
                deliveryDistance
                lastScannedDriverAccountId
                lastScannedDriverName
                lastAssignedDriverAccountId
                lastAssignedDriverName
            }
            orderWms {
                ttSortingCode
                sortingRule
                ttBizType
                sortingSegmentCode
                lastMileSortingCode
            }
            deliveryRevokeStatus {
                canRevoke
                revokeDeadline
            }
            orderType
            canEditAddressLocation
            addressLocationChangePending
            pendingLatitude
            pendingLongitude
`;

export const GET_ORDER_DETAIL_FOR_CENTRAL = gql`
    query CentralQuery($orderId: Long!) {
        getOrderDetailForCentral(orderId: $orderId) {
            ${ORDER_DETAIL_FIELDS}
        }
    }
`;

export interface CentralOrderDetailResponse {
    getOrderDetailForCentral: OrderDetailType;
}
